import React, { useState, useRef, useEffect, useMemo } from "react";
import BannerSection from "../../components/common/BannerSection";
import { Box, Typography, Button, Grid, Paper, Snackbar, Alert } from "@mui/material";
import AuthenticationTextInput from "../../components/common/TextInput/AuthenticationTextInput";
import { Images } from "../../utils/Images";
import { useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import DonateButton from "../../components/common/DonateButton";
import ReCAPTCHA from "react-google-recaptcha";
import NewsLogoSection from "../HomePage/NewsLogoSection";
import { useDispatch, useSelector } from "react-redux";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";
import { submitContactFormAction, clearContactState } from "../../store/slices/contactSlice";
import {
  sanitizeIndianMobileInput,
  validateIndianMobile,
} from "../../utils/phoneValidation";

const CustomDonateButton = styled(Button)(({ theme, isMobile, isTablet }) => ({
  backgroundColor: "#F04951",
  color: "#FFFFFF",
  borderRadius: "10px",
  boxShadow: "0px 4px 0px #141414",
  // padding: isMobile ? "12px 16px" : isTablet ? "14px 20px" : "15px 24px",
  width: isMobile ? "100%" : "160px", // 100% width for responsiveness
  height: isMobile ? "44px" : isTablet ? "44px" : "44px",
  fontFamily: "Red Hat Display, sans-serif",
  fontWeight: 700,
  fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px",
  lineHeight: isMobile ? "13px" : isTablet ? "14px" : "15px",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: isMobile ? "6px" : isTablet ? "7px" : "8px",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#d63f46",
    boxShadow: "0px 4px 0px #141414",
  },
  "&:active": {
    boxShadow: "0px 2px 0px #141414",
    transform: "translateY(2px)",
  },
}));
const ContactUsPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTab = useMediaQuery(theme.breakpoints.down("md"));
  const isLap = useMediaQuery(theme.breakpoints.down("lg"));
  const [form, setForm] = useState({
    fullName: "",
    contactNumber: "",
    email: "",
    subject: "",
    message: "",
  });

  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef();
  
  // Get contact state from Redux
  const { loading, success, error } = useSelector(state => state.contact);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  // CMS: contact info from id 69
  const cmsHome = useSelector((state) => state.cms.home);
  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);
  const contactCms = useMemo(() => {
    const sections = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const section = sections.find((s) => s?.id === 69);
    const related = Array.isArray(section?.relatedCMS) ? section.relatedCMS : [];
    const firstRelated = related.find((r) => r && (r.status === 1 || r.status === true));
    const introText = firstRelated?.description?.trim() || "";
    return {
      phone: section?.title || "",
      email: section?.sub_title || "",
      address: section?.description || "",
      introText: introText || "",
    };
  }, [cmsHome]);

  const handleChange = (field) => (e) => {
    const value =
      field === "contactNumber"
        ? sanitizeIndianMobileInput(e.target.value)
        : e.target.value;
    setForm({ ...form, [field]: value });
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken("");
  };

  const handleCaptchaError = () => {
    setCaptchaToken("");
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
    if (success || error) {
      dispatch(clearContactState());
    }
  };

  // Effect to handle Redux state changes
  useEffect(() => {
    if (success) {
      setSnackbar({
        open: true,
        message: "Your message has been sent successfully!",
        severity: "success"
      });
      
      // Reset form and captcha
      setForm({
        fullName: "",
        contactNumber: "",
        email: "",
        subject: "",
        message: "",
      });
      setCaptchaToken("");
      captchaRef.current.reset();
    }
    
    if (error) {
      setSnackbar({
        open: true,
        message: error || "Failed to send your message. Please try again later.",
        severity: "error"
      });
    }
  }, [success, error, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setSnackbar({
        open: true,
        message: "Please complete the captcha verification",
        severity: "error"
      });
      return;
    }

    // Basic validation
    if (!form.fullName || !form.email || !form.contactNumber || !form.subject || !form.message) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error"
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setSnackbar({
        open: true,
        message: "Please enter a valid email address",
        severity: "error"
      });
      return;
    }

    const phoneValidation = validateIndianMobile(form.contactNumber);
    if (!phoneValidation.isValid) {
      setSnackbar({
        open: true,
        message: phoneValidation.error,
        severity: "error",
      });
      return;
    }

    // Prepare data for API
    const contactData = {
      name: form.fullName,
      email: form.email,
      contactNumber: phoneValidation.digits,
      subject: form.subject,
      message: form.message,
      recaptcha_token: captchaToken // Include reCAPTCHA token
    };
    
    // Dispatch Redux action to submit form
    dispatch(submitContactFormAction(contactData));
  };

  return (
    <Box sx={{}}>
      <Box sx={{ mb: isMobile ? "10px" : "40px", mt: isMobile ? "0" : "30px" }}>
        <BannerSection
          title="Contact Us"
          subtitle="Your enquiry is valuable for us"
        />
      </Box>
      <Box
        sx={{
          padding: isTab ? "20px 15px" : "30px",
          background: isMobile ? "14px" : "rgba(20, 20, 20, 0.02)",
          maxWidth: "1440px",
          margin: "0 auto",
          borderRadius: "20px",
          mb: isMobile ? "20px" : "40px",
        }}
      >
        <Box
          container
          spacing={4}
          sx={{
            display: "flex",
            gap: "24px",
            flexDirection: isTab ? "column-reverse" : "row",
            flexWrap: "wrap-reverse",
          }}
        >
          {/* Left Side */}
          <Box sx={{ flex: 1, minWidth: 0, width: isTab ? "100%" : "auto" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                marginBottom: "12px",
                fontSize: isMobile ? "20px" : "26px",
                color: "#1D1D1B",
              }}
            >
              Get in touch
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                marginBottom: isMobile ? "20px" : "30px",
                fontSize: isMobile ? "12px" : "14px",
                color: "#979796",
                maxWidth: "500px",
              }}
            >
              {contactCms.introText}
            </Typography>
            <Paper
              elevation={0}
              sx={{
                padding: isMobile ? "16px 20px" : "14px 20px",
                mb: "20px",
                display: "flex",
                alignItems: "center",
                gap: 2,
                boxShadow: "0 2px 16px 0 rgba(0, 0, 0, 0.06)",
                borderRadius: "12px",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#9CBA4D",
                  borderRadius: "5.169px",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img src={Images.hospital} alt="Images.hospital" />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: "500",
                    fontSize: isMobile ? "10px" : "12px",
                    color: "#686866",
                    marginBottom: "8px",
                  }}
                >
                  Location
                </Typography>
                <Typography
                  sx={{
                    fontSize: isMobile ? "12px" : "14px",
                    color: "#1D1D1B",
                    fontWeight: "600",
                  }}
                >
                  {contactCms.address}
                </Typography>
              </Box>
            </Paper>
            <Box
              container
              item
              sx={{
                display: "flex",
                gap: isLap ? "0px" : "20px",
                flexDirection: isLap ? "column" : "row",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Paper
                  sx={{
                    padding: "14px 20px",
                    mb: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    boxShadow: "0 2px 16px 0 rgba(0, 0, 0, 0.06)",
                    borderRadius: "12px",
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#9CBA4D",
                      borderRadius: "5.169px",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={Images.phoneCall} alt="Images.phoneCall" />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: "500",
                        fontSize: isMobile ? "10px" : "12px",
                        color: "#686866",
                        marginBottom: "8px",
                      }}
                    >
                      Helpline Number
                    </Typography>
                    <Typography
                      component="a"
                      href={`tel:${contactCms.phone?.replace(/\s+/g, '')}`}
                      sx={{
                        fontSize: isMobile ? "12px" : "14px",
                        color: "#1D1D1B",
                        fontWeight: "600",
                        textDecoration: "none",
                        cursor: "pointer",
                        "&:hover": {
                          color: "#9CBA4D",
                          textDecoration: "none",
                        },
                      }}
                    >
                      {contactCms.phone}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Paper
                  sx={{
                    padding: "14px 20px",
                    mb: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    boxShadow: "0 2px 16px 0 rgba(0, 0, 0, 0.06)",
                    borderRadius: "12px",
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#9CBA4D",
                      borderRadius: "5.169px",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={Images.mail} alt="Images.mail" />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: "500",
                        fontSize: isMobile ? "10px" : "12px",
                        color: "#686866",
                        marginBottom: "8px",
                      }}
                    >
                      Email
                    </Typography>
                    <Typography
                      component="a"
                      href={`mailto:${contactCms.email}`}
                      sx={{
                        fontSize: isMobile ? "12px" : "14px",
                        color: "#1D1D1B",
                        fontWeight: "600",
                        textDecoration: "none",
                        cursor: "pointer",
                        "&:hover": {
                          color: "#9CBA4D",
                          textDecoration: "none",
                        },
                      }}
                    >
                      {contactCms.email}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>
            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                mt: "14px",
                height: isMobile ? "175px" : "275px",
              }}
            >
              <iframe
                title="Google Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4417.05769680195!2d72.96662839999999!3d26.249462299999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39418f36aee43149%3A0xbf7a963865c05a8f!2sTrue%20Hope%20Foundation!5e1!3m2!1sen!2sin!4v1773659157622!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>
          </Box>

          {/* Right Side - Form */}
          <Box sx={{ flex: 1, position: "relative" }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 4 },
                borderRadius: 3,
                height: "100%",
                position: "relative",
                background: "#fff",
                boxShadow:
                  "0 45px 13px 0 rgba(0, 0, 0, 0.00), 0 3px 7px 0 rgba(0, 0, 0, 0.09)",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  display: "flex",
                  gap: "8px",
                  fontWeight: 700,
                  marginBottom: isMobile ? "23px" : "26px",
                  fontSize: isMobile ? "14px" : "22px",
                  color: "#1D1D1B",
                }}
              >
                <img
                  src={Images.streamlineSharpStar}
                  alt="Images.streamlineSharpStar"
                />{" "}
                Fill the details
              </Typography>
              <form onSubmit={handleSubmit}>
                <Box container sx={{ gap: "24px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: isMobile ? "0px" : "21px",
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    <Box item sx={{ flex: 1 }}>
                      <AuthenticationTextInput
                        label="Full Name"
                        value={form.fullName}
                        onChange={handleChange("fullName")}
                        required
                        placeholder="Enter Full Name"
                        sx={{ fontWeight: "600" }}
                      />
                    </Box>
                    <Box item sx={{ flex: 1 }}>
                      <AuthenticationTextInput
                        label="Contact number"
                        value={form.contactNumber}
                        onChange={handleChange("contactNumber")}
                        phoneInput
                        required
                        placeholder="Enter Contact number"
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: isMobile ? "0px" : "21px",
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    <Box item sx={{ flex: 1 }}>
                      <AuthenticationTextInput
                        label="Email ID"
                        value={form.email}
                        onChange={handleChange("email")}
                        required
                        placeholder="Enter Email ID"
                      />
                    </Box>
                    <Box item sx={{ flex: 1 }}>
                      <AuthenticationTextInput
                        label="Subject"
                        value={form.subject}
                        onChange={handleChange("subject")}
                        required
                        placeholder="Enter Subject"
                      />
                    </Box>
                  </Box>
                  <Box item>
                    <AuthenticationTextInput
                      label="Message"
                      value={form.message}
                      onChange={handleChange("message")}
                      required
                      placeholder="Enter Your message"
                      multiline
                      minRows={3}
                    />
                  </Box>
                  <Box item>
                    <Box sx={{ mb: 2 }}>
                      <ReCAPTCHA
                        sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                        onChange={handleCaptchaVerify}
                        onExpired={handleCaptchaExpire}
                        onErrored={handleCaptchaError}
                        ref={captchaRef}
                      />
                    </Box>
                  </Box>
                  <Box item>
                    <CustomDonateButton 
                      isMobile={isMobile}
                      disabled={loading}
                      type="submit"
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </CustomDonateButton>
                  </Box>
                </Box>
              </form>
              
              {/* Feedback Snackbar */}
              <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              >
                <Alert 
                  onClose={handleSnackbarClose} 
                  severity={snackbar.severity}
                  sx={{ width: '100%' }}
                >
                  {snackbar.message}
                </Alert>
              </Snackbar>
              {/* Decorative hand icon */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: "30px",
                  right: 0,
                  display: isMobile ? "none" : "block",
                }}
              >
                <img
                  src={Images.contactusFormElement}
                  alt="Images.contactusFormElement"
                />
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
      {/* News Logo Section */}
      <Box>
        <NewsLogoSection />
      </Box>
    </Box>
  );
};

export default ContactUsPage;
